import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Checkbox,
  SimpleGrid,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";

const LANGUAGES_PAGE_ID = PAGE_IDS.LANGUAGES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

function CreateLanguage({ onCreated }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const errors = {};
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      errors.code = t("Language code is required");
    } else if (trimmedCode.length < 2 || trimmedCode.length > 10) {
      errors.code = t("Language code must be 2-10 characters (e.g. fr, en-US)");
    }
    if (!name.trim()) errors.name = t("Language name is required");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        page_id: LANGUAGES_PAGE_ID,
        code: code.trim(),
        name: name.trim(),
        isActive,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/language`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create language");
      }
      setCode("");
      setName("");
      setIsActive(true);
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create language. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isInvalid={!!fieldErrors.code}>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Code")}</FormLabel>
            <Input isRequired placeholder={t("e.g. fr")} value={code} onChange={(e) => setCode(e.target.value)} {...fieldProps} />
            <FormErrorMessage>{fieldErrors.code}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!fieldErrors.name}>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Name")}</FormLabel>
            <Input isRequired placeholder={t("e.g. French")} value={name} onChange={(e) => setName(e.target.value)} {...fieldProps} />
            <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <Checkbox mt="16px" isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} colorScheme="brand">
          {t("Active (available to authors immediately)")}
        </Checkbox>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Add Language")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateLanguage;
